import { ProjectTask } from "../../../interfaces/ProjectTask";

/**
 * Os filtros do cronograma — período e situação — como funções puras.
 *
 * Ficam fora do `TaskGantt` de propósito: o botão de exportar PDF fica ao lado do gráfico e recebe
 * a MESMA lista. Com o filtro morando dentro do componente, filtrar a tela e exportar produziria um
 * PDF com tudo — e a pessoa só descobriria depois de mandar o arquivo para alguém.
 */

/** Janela do cronograma. `null` em qualquer ponta = sem limite daquele lado. */
export interface GanttRange {
  start: Date | null;
  end: Date | null;
}

export type GanttPeriod = "all" | "fromToday" | "month" | "quarter";
export type GanttStatus = "all" | "open" | "overdue" | "done";

export const GANTT_PERIOD_LABELS: Record<GanttPeriod, string> = {
  all: "Todo o período",
  fromToday: "De hoje em diante",
  month: "Este mês",
  quarter: "Este trimestre",
};

export const GANTT_STATUS_LABELS: Record<GanttStatus, string> = {
  all: "Todas as situações",
  open: "Só em aberto",
  overdue: "Só atrasadas",
  done: "Só concluídas",
};

const startOfDay = (d: Date) => new Date(d.getFullYear(), d.getMonth(), d.getDate());

/**
 * Parse de data **como hora local**.
 *
 * ⚠ `new Date("2026-08-03")` é interpretado pelo JS como meia-noite **UTC** — que em UTC-3 é o dia
 * 2 às 21h. Sem isto, todo filtro de período erraria por um dia para tarefas cujo campo vem sem
 * hora, e o erro só apareceria na virada do mês. O sufixo de hora força a leitura local; datas que
 * já vêm com hora (`...T09:00:00`) passam direto.
 */
const parseLocal = (valor: string): Date =>
  /^\d{4}-\d{2}-\d{2}$/.test(valor) ? new Date(valor + "T00:00:00") : new Date(valor);

/**
 * A janela de cada período, ancorada em <c>hoje</c>.
 *
 * <c>fromToday</c> é a resposta direta ao problema que motivou o filtro — cronograma de tarefa
 * antiga poluindo a tela — e por isso é aberta à direita: quem quer "esconder o passado" não quer,
 * junto, esconder o que ainda vai acontecer.
 */
export function periodRange(period: GanttPeriod, today: Date): GanttRange | null {
  const hoje = startOfDay(today);

  switch (period) {
    case "all":
      return null;
    case "fromToday":
      return { start: hoje, end: null };
    case "month":
      return {
        start: new Date(hoje.getFullYear(), hoje.getMonth(), 1),
        end: new Date(hoje.getFullYear(), hoje.getMonth() + 1, 0),
      };
    case "quarter": {
      const primeiroMes = Math.floor(hoje.getMonth() / 3) * 3;
      return {
        start: new Date(hoje.getFullYear(), primeiroMes, 1),
        end: new Date(hoje.getFullYear(), primeiroMes + 3, 0),
      };
    }
  }
}

/**
 * O intervalo que a tarefa ocupa no gráfico: o cronograma dia-a-dia quando existe, senão as datas
 * de início/entrega. `null` = a tarefa não aparece no gráfico, e nesse caso o período não a exclui
 * (excluí-la aqui seria o filtro de período decidindo sobre algo que ele não mede).
 */
export function taskSpan(task: ProjectTask, lines?: { date: string }[]): GanttRange | null {
  if (lines && lines.length) {
    const datas = lines.map((l) => l.date).sort();
    return { start: parseLocal(datas[0]), end: parseLocal(datas[datas.length - 1]) };
  }

  if (!task.startDate && !task.dueDate) return null;

  return {
    start: startOfDay(parseLocal(task.startDate || task.dueDate!)),
    end: startOfDay(parseLocal(task.dueDate || task.startDate!)),
  };
}

/** Intersecção de intervalos, com ponta nula significando "sem limite". */
export function intersects(span: GanttRange | null, range: GanttRange | null): boolean {
  if (range === null) return true;
  if (span === null) return true;

  if (range.start && span.end && span.end < range.start) return false;
  if (range.end && span.start && span.start > range.end) return false;

  return true;
}

export function matchesStatus(task: ProjectTask, status: GanttStatus): boolean {
  switch (status) {
    case "all":
      return true;
    case "open":
      // "Em aberto" é tudo que ainda pede ação — inclusive o que está PARADO. Uma tarefa bloqueada
      // some do radar se cair no mesmo balde de concluída, e é justamente ela que precisa aparecer.
      return task.statusCategory !== "Done" && task.statusCategory !== "Cancelled";
    case "overdue":
      return task.scheduleState === "overdue";
    case "done":
      return task.statusCategory === "Done";
  }
}

/**
 * Aplica os dois filtros e **devolve os ancestrais das sobreviventes junto**.
 *
 * ⚠ Os ancestrais não são cortesia: o `TaskGantt` monta as linhas descendo a partir das tarefas sem
 * pai. Uma subtarefa cujo épico foi filtrado fora não teria por onde ser alcançada e sumiria da
 * tela — o filtro de situação apagaria silenciosamente a tarefa em aberto que ele deveria mostrar,
 * só porque o épico dela está concluído.
 */
export function filterGanttTasks(
  tasks: ProjectTask[],
  schedules: Record<number, { date: string }[]>,
  period: GanttPeriod,
  status: GanttStatus,
  today: Date = new Date()
): ProjectTask[] {
  if (period === "all" && status === "all") return tasks;

  const range = periodRange(period, today);
  const byId = new Map(tasks.map((t) => [t.id, t]));

  const manter = new Set<number>();
  tasks.forEach((t) => {
    if (!matchesStatus(t, status)) return;
    if (!intersects(taskSpan(t, schedules[t.id]), range)) return;

    manter.add(t.id);

    let pai = t.parentTaskId ?? null;
    let guarda = 0;
    while (pai != null && guarda++ < 12) {
      manter.add(pai);
      pai = byId.get(pai)?.parentTaskId ?? null;
    }
  });

  return tasks.filter((t) => manter.has(t.id));
}
